import type {
  Agent,
  EnterprisePosture,
  EstateAsset,
  EstateAssetType,
  EstateEnvironment,
  EstatePolicyStatus,
  RelationshipEdge,
  RelationshipGraph,
  RelationshipNode,
} from "@/api/temp/TrustAIExecutiveService";
import { dataPlatformGovernance } from "./dataGovernance";

/**
 * `data-sources` is summed from the data-governance seed rather than authored
 * here: the estate headline, the Data Estate tab and the discovery fixtures in
 * `./lifecycle` all report this one number, so stating it twice would let them
 * drift.
 */
const DATA_SOURCE_COUNT = dataPlatformGovernance.reduce(
  (total, platform) => total + platform.dataSourceTotal,
  0,
);

export const ESTATE_COUNTS = {
  "ai-agents": 612,
  "mcp-servers": 84,
  // Individual agent tools, across the 27 registries below.
  tools: 486,
  "data-sources": DATA_SOURCE_COUNT,
  "tool-registries": 27,
  "data-platforms": 15,
  "enterprise-datasets": 148,
  "sensitive-datasets": 63,
  "identity-sources": 18,
  gateways: 17,
} satisfies Record<EstateAssetType, number>;

const OWNERS = [
  "AI Platform",
  "Data Governance",
  "Security Engineering",
  "Finance Technology",
  "People Systems",
  "Customer Operations",
];

const BUSINESS_UNITS = [
  "Engineering",
  "Finance",
  "Human Resources",
  "Customer Support",
  "Product Design",
  "Risk & Compliance",
];

const ENVIRONMENTS: EstateEnvironment[] = [
  "Production",
  "Staging",
  "Development",
];

const DATA_PLATFORMS = [
  "Snowflake — Production Analytics",
  "Databricks — EU Customer Workspace",
  "SharePoint — Finance Restricted",
  "Microsoft Fabric — Enterprise BI",
  "Amazon S3 — Customer Archive",
  "Google BigQuery — Growth Analytics",
  "Workday — EU People Data",
  "PostgreSQL — Product Operations",
  "Salesforce — Customer 360",
  "ServiceNow — Enterprise Operations",
  "Azure Data Lake — Research",
  "Oracle — Financial Close",
  "MongoDB — Digital Experiences",
  "Redshift — Revenue Intelligence",
  "MySQL — Supplier Operations",
];

const TYPE_LABEL: Record<EstateAssetType, string> = {
  "ai-agents": "AI Agent",
  "mcp-servers": "MCP Server",
  tools: "Tool",
  "data-sources": "Data Source",
  "tool-registries": "Tool Registry",
  "data-platforms": "Data Platform",
  "enterprise-datasets": "Mapped Catalog",
  "sensitive-datasets": "Sensitive Asset",
  "identity-sources": "Identity Source",
  gateways: "Gateway",
};

const pad = (value: number): string => String(value).padStart(3, "0");

const baseAsset = (
  type: EstateAssetType,
  index: number,
  overrides: Partial<EstateAsset> = {},
): EstateAsset => ({
  id: `${type}-${pad(index + 1)}`,
  name: `${TYPE_LABEL[type]} ${pad(index + 1)}`,
  type,
  owner: OWNERS[index % OWNERS.length],
  businessUnit: BUSINESS_UNITS[index % BUSINESS_UNITS.length],
  environment: ENVIRONMENTS[index % ENVIRONMENTS.length],
  status: index % 11 === 0 ? "Needs attention" : "Protected",
  platform: TYPE_LABEL[type],
  description: `${TYPE_LABEL[type]} discovered and mapped by TrustLogix.`,
  violationCount: index % 13 === 0 ? (index % 3) + 1 : 0,
  runtimeActive: index % 4 !== 0,
  ...overrides,
});

const buildAgentAssets = (
  curatedAgents: Agent[],
  posture: EnterprisePosture,
): EstateAsset[] => {
  const secureTarget = posture.policyPosture.secure;
  const curatedSecure = curatedAgents.filter(
    agent => agent.policyPostureState === "SECURE",
  ).length;
  const generatedSecureTarget = secureTarget - curatedSecure;

  const curated = curatedAgents.map((agent, index) =>
    baseAsset("ai-agents", index, {
      id: agent.id,
      name: agent.name,
      platform: agent.platform,
      description: agent.riskState,
      status:
        agent.policyPostureState === "SECURE"
          ? "Protected"
          : "Needs attention",
      violationCount: agent.policyPostureState === "SECURE" ? 0 : 1,
      runtimeActive: true,
    }),
  );

  const generated = Array.from(
    { length: ESTATE_COUNTS["ai-agents"] - curated.length },
    (_, generatedIndex) => {
      const index = curated.length + generatedIndex;
      const isSecure = generatedIndex < generatedSecureTarget;
      return baseAsset("ai-agents", index, {
        status: isSecure ? "Protected" : "Needs attention",
        violationCount: isSecure ? (index % 29 === 0 ? 1 : 0) : 1,
        platform: [
          "OpenAI",
          "Microsoft Copilot",
          "Claude",
          "Databricks",
          "Amazon Bedrock",
          "Google Vertex AI",
          "Snowflake Cortex",
          "ServiceNow",
        ][index % 8],
      });
    },
  );

  return [...curated, ...generated];
};

/**
 * One platform name per data source, repeated `dataSourceTotal` times, so the
 * 45 sources in the inventory are the same 45 the Data Estate tab counts and
 * each one names the platform it belongs to.
 */
const DATA_SOURCE_PLATFORMS: string[] = dataPlatformGovernance.flatMap(
  platform => Array.from({ length: platform.dataSourceTotal }, () => platform.name),
);

/** Real-world names for the types that have them; generated otherwise. */
const namedOverrides = (
  type: EstateAssetType,
  index: number,
): Partial<EstateAsset> => {
  if (type === "data-platforms") {
    return {
      name: DATA_PLATFORMS[index],
      platform: DATA_PLATFORMS[index].split(" — ")[0],
    };
  }
  if (type === "data-sources") {
    const platform = DATA_SOURCE_PLATFORMS[index];
    return { name: `${platform} ${TYPE_LABEL[type]} ${pad(index + 1)}`, platform };
  }
  return {};
};

export const buildEstateAssets = (
  posture: EnterprisePosture,
  curatedAgents: Agent[],
): EstateAsset[] => {
  const agents = buildAgentAssets(curatedAgents, posture);
  // Derived from the counts rather than hand-listed: a metric added to
  // `ESTATE_COUNTS` generates its assets automatically, so its tile can never
  // open an inventory drawer with nothing in it. Agents are excluded because
  // they have their own builder, which reconciles them with the posture.
  const otherTypes = (
    Object.keys(ESTATE_COUNTS) as EstateAssetType[]
  ).filter(type => type !== "ai-agents");

  const otherAssets = otherTypes.flatMap(type =>
    Array.from({ length: ESTATE_COUNTS[type] }, (_, index) =>
      baseAsset(type, index, namedOverrides(type, index)),
    ),
  );

  return [...agents, ...otherAssets];
};

const layerForType = (
  type: EstateAssetType,
): RelationshipNode["layer"] => {
  if (type === "identity-sources") return "identity";
  if (type === "ai-agents") return "agent";
  if (type === "gateways") return "gateway";
  if (type === "mcp-servers" || type === "tool-registries" || type === "tools") {
    return "tool";
  }
  return "data";
};

const toNode = (asset: EstateAsset): RelationshipNode => ({
  id: `node-${asset.id}`,
  assetId: asset.id,
  label: asset.name,
  layer: layerForType(asset.type),
  assetType: asset.type,
  owner: asset.owner,
  businessUnit: asset.businessUnit,
  environment: asset.environment,
  policyStatus: asset.status,
  violationCount: asset.violationCount,
  runtimeActive: asset.runtimeActive,
  description: asset.description,
});

const buildLayerEdges = (
  sources: EstateAsset[],
  targets: EstateAsset[],
  prefix: string,
): RelationshipEdge[] =>
  sources.map((source, index) => {
    const target = targets[index % targets.length];
    const policyStatus: EstatePolicyStatus =
      source.status === "Needs attention" || target.status === "Needs attention"
        ? "Needs attention"
        : "Protected";
    return {
      id: `${prefix}-${source.id}-${target.id}`,
      source: `node-${source.id}`,
      target: `node-${target.id}`,
      runtimeActive: source.runtimeActive && target.runtimeActive,
      policyStatus,
      violationCount: source.violationCount + target.violationCount,
    };
  });

export const buildRelationshipGraph = (
  assets: EstateAsset[],
): RelationshipGraph => {
  const byType = (type: EstateAssetType) =>
    assets.filter(asset => asset.type === type);
  const identities = byType("identity-sources");
  const agents = byType("ai-agents");
  const gateways = byType("gateways");
  const servers = byType("mcp-servers");
  const registries = byType("tool-registries");
  const platforms = byType("data-platforms");
  const catalogs = byType("enterprise-datasets");
  const sensitiveAssets = byType("sensitive-datasets");

  return {
    nodes: assets.map(toNode),
    edges: [
      ...buildLayerEdges(agents, identities, "identity-agent").map(edge => ({
        ...edge,
        source: edge.target,
        target: edge.source,
      })),
      ...buildLayerEdges(agents, gateways, "agent-gateway"),
      ...buildLayerEdges(servers, gateways, "gateway-server").map(edge => ({
        ...edge,
        source: edge.target,
        target: edge.source,
      })),
      ...buildLayerEdges(servers, registries, "server-registry"),
      ...buildLayerEdges(registries, platforms, "registry-platform"),
      ...buildLayerEdges(catalogs, platforms, "platform-catalog").map(edge => ({
        ...edge,
        source: edge.target,
        target: edge.source,
      })),
      ...buildLayerEdges(sensitiveAssets, catalogs, "catalog-sensitive").map(
        edge => ({ ...edge, source: edge.target, target: edge.source }),
      ),
    ],
    totals: { ...ESTATE_COUNTS },
  };
};

